import { CheckCircle2, Lock } from "lucide-react";
import type { V2Bag, V2Value } from "@/features/applications/api/applications.api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatDateTime } from "@/utils/dates";
import { onlineOrderingNeedsSecondApp } from "../pricing";
import { rateExceptionOf, rateExceptionCovers } from "../model";

const text = (v: V2Value | undefined): string | null => {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  return s === "" ? null : s;
};

const pct = (v: V2Value | undefined) => (text(v) ? `${text(v)}%` : null);
const usd = (v: V2Value | undefined) => {
  const s = text(v);
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? `$${n.toFixed(2)}` : `$${s}`;
};
const join = (parts: (string | null)[], sep = " + ") => parts.filter(Boolean).join(sep) || null;

export function pricingSummaryRows(bag: V2Bag): { label: string; value: string }[] {
  const rows: { label: string; value: string | null }[] = [
    { label: "Pricing program", value: text(bag.pricingProgram) },
    { label: "Rate", value: join([pct(bag.pricingRatePct), usd(bag.pricingPerTxnFee) && `${usd(bag.pricingPerTxnFee)} per transaction`]) },
  ];
  if (bag.amexCustomRate === true) rows.push({ label: "American Express", value: pct(bag.amexRatePct) });
  if (bag.pinDebitEnabled === true)
    rows.push({ label: "PIN debit", value: join([pct(bag.pinDebitRatePct), usd(bag.pinDebitPerItemFee) && `${usd(bag.pinDebitPerItemFee)} per item`]) });
  if (bag.onlineOrdering === true)
    rows.push({
      label: "Online ordering",
      value: onlineOrderingNeedsSecondApp(bag.pricingProgram, bag.pricingRatePct) ? "Separate MPA · Flat Rate 3.3% + $0.30" : "Provided by BLogic",
    });
  if (bag.sensaInvoices === true) rows.push({ label: "SensaInvoices", value: "On" });
  if (bag.acceptAch === true)
    rows.push({ label: "ACH", value: join([pct(bag.achRatePct), usd(bag.achFeePerTxn) && `${usd(bag.achFeePerTxn)} per transaction`]) });
  const exception = rateExceptionOf(bag);
  if (exception?.status === "Approved" && rateExceptionCovers(bag, exception))
    rows.push({ label: "Rate exception", value: `Approved${exception.decidedBy ? ` by ${exception.decidedBy}` : ""}` });
  rows.push({ label: "Notes for back office", value: text(bag.pricingNotesForBackOffice) });
  return rows.filter((r): r is { label: string; value: string } => r.value !== null);
}

export function PricingSummary({ bag, locked, onEdit }: { bag: V2Bag; locked: boolean; onEdit?: () => void }) {
  const enteredBy = text(bag.pricingEnteredBy);
  const enteredRole = text(bag.pricingEnteredByRole);
  const enteredAt = text(bag.pricingEnteredAt);
  const who = [
    enteredAt && formatDateTime(enteredAt),
    enteredBy && `by ${enteredBy}${enteredRole ? ` (${enteredRole})` : ""}`,
  ].filter(Boolean).join(" ");

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0 border-b py-3">
        <div>
          <CardTitle className="flex items-center gap-2 text-base font-semibold text-slate-900">
            <CheckCircle2 className="h-5 w-5 text-emerald-600" aria-hidden="true" />
            Pricing configured
          </CardTitle>
          {who && <p className="mt-0.5 text-xs text-slate-500">Configured {who}</p>}
        </div>
        {locked ? (
          <span className="flex items-center gap-1 text-sm text-slate-500"><Lock className="h-4 w-4" aria-hidden="true" /> Locked</span>
        ) : onEdit ? (
          <Button type="button" variant="outline" size="sm" onClick={onEdit}>Edit pricing</Button>
        ) : null}
      </CardHeader>
      <CardContent className="p-5">
        <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-[200px_1fr]">
          {pricingSummaryRows(bag).map((r) => (
            <div key={r.label} className="contents">
              <dt className="text-slate-500">{r.label}</dt>
              <dd className="whitespace-pre-wrap text-slate-900">{r.value}</dd>
            </div>
          ))}
        </dl>
      </CardContent>
    </Card>
  );
}
